
import React, { forwardRef, useImperativeHandle } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { getEditorConfig } from './EditorConfig';
import ToolbarActions from './ToolbarActions';
import { sanitizeHtml } from '@/utils/security';

export interface RichTextEditorRef {
  getContent: () => string;
  setContent: (content: string) => void;
  focus: () => void;
}

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  className?: string;
}

const RichTextEditor = forwardRef<RichTextEditorRef, RichTextEditorProps>(
  ({ content, onChange, placeholder = "Start writing...", className = "" }, ref) => {
    const editor = useEditor({
      ...getEditorConfig(placeholder),
      content: sanitizeHtml(content), // Sanitize initial content
      onUpdate: ({ editor }) => {
        const htmlContent = editor.getHTML();
        // Sanitize content before passing to onChange
        const sanitizedContent = sanitizeHtml(htmlContent);
        onChange(sanitizedContent);
      },
    });

    useImperativeHandle(ref, () => ({
      getContent: () => {
        if (!editor) return '';
        const htmlContent = editor.getHTML();
        return sanitizeHtml(htmlContent); // Sanitize on get
      },
      setContent: (newContent: string) => {
        if (editor) {
          const sanitizedContent = sanitizeHtml(newContent);
          editor.commands.setContent(sanitizedContent);
        }
      },
      focus: () => {
        editor?.commands.focus();
      }
    }));

    if (!editor) {
      return (
        <div className={`border rounded-md p-4 ${className}`}>
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      );
    }

    return (
      <div className={`border rounded-md ${className}`}>
        {/* Toolbar */}
        <div className="border-b p-2">
          <div className="flex flex-wrap items-center gap-1">
            <ToolbarActions editor={editor} />
          </div>
        </div>

        {/* Editor Content */}
        <div className="prose prose-sm max-w-none p-4 min-h-[200px] focus-within:outline-none">
          <EditorContent 
            editor={editor} 
            className="min-h-[150px] focus:outline-none [&_.ProseMirror]:focus:outline-none [&_.ProseMirror]:min-h-[150px]"
          />
        </div>

        {/* Security Notice */}
        <div className="border-t bg-gray-50 px-4 py-2">
          <p className="text-xs text-muted-foreground">
            Content is automatically sanitized for security. Only safe HTML tags and attributes are allowed.
          </p>
        </div>
      </div>
    );
  }
);

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
